import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Sparkles, Star } from 'lucide-react';
import { SlideLeft, SlideRight } from '../../../components/shared/Motion';
import { EditableText, EditableImage, HrefPill, useEditMode } from '../../components';
import { Icon as IconByName, getIcon } from '../iconMap';

/**
 * hero-carousel — Home only.
 * In view mode: pixel-identical to HomePage.js hero.
 * In edit mode: text + images are inline-editable; carousel auto-rotation
 * is disabled so editors can interact with each slide.
 */
function HeroCarousel({ data, editing, onChange }) {
  const { editMode } = useEditMode();
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const items = data.items || [];
  const [heroIdx, setHeroIdx] = useState(0);
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroParallaxY = useTransform(scrollY, [0, 800], [0, 200]);
  const heroOpacity = useTransform(scrollY, [0, 600], [1, 0.2]);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return undefined;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
      el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
    };
    el.addEventListener('pointermove', onMove);
    return () => el.removeEventListener('pointermove', onMove);
  }, []);

  useEffect(() => {
    if (editing) return undefined;
    if (items.length <= 1) return undefined;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % items.length), 6500);
    return () => clearInterval(t);
  }, [editing, items.length]);

  if (!items.length) return null;
  const slide = items[heroIdx] || items[0];
  const StatIcon = getIcon(data.stat?.icon || 'Trophy');

  const updateSlide = (patch) => {
    if (!onChange) return;
    const nextItems = items.map((it, i) => (i === heroIdx ? { ...it, ...patch } : it));
    onChange({ ...data, items: nextItems });
  };
  const updateSlideTitle = (i, text) => {
    const tp = [...(slide.title_parts || ['', '', ''])];
    tp[i] = text;
    updateSlide({ title_parts: tp });
  };

  return (
    <section ref={heroRef} className="relative h-screen min-h-[720px] w-full overflow-hidden bg-olive-deep">
      <motion.div style={{ y: heroParallaxY, opacity: heroOpacity }} className="absolute inset-0">
        {items.map((s, i) => (
          <motion.div
            key={i}
            initial={false}
            animate={{ opacity: heroIdx === i ? 1 : 0, scale: heroIdx === i ? 1.05 : 1.15 }}
            transition={{ opacity: { duration: 1.2 }, scale: { duration: 8, ease: 'easeOut' } }}
            className="absolute inset-0 overflow-hidden"
            style={{ filter: 'blur(6px) saturate(0.7)' }}
          >
            <img
              src={s.bg_image?.src || s.image?.src}
              alt={s.bg_image?.alt || s.image?.alt || ''}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-br from-olive-footer/98 via-olive-deep/96 to-black/95" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_40%,transparent_0%,rgba(0,0,0,0.55)_70%)]" />
      </motion.div>

      <div className="absolute inset-6 sm:inset-10 lg:inset-12 rounded-[28px] overflow-hidden border border-white/10 pointer-events-none" />
      <div className="absolute inset-0 bg-grid-olive [background-size:48px_48px] opacity-20 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none opacity-90" style={{ background: 'radial-gradient(700px circle at var(--mx,30%) var(--my,40%), rgba(197,120,99,0.25), transparent 55%)' }} />

      <motion.div animate={{ x: [0, 50, 0], y: [0, -30, 0] }} transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }} className="absolute top-1/4 right-[10%] w-[420px] h-[420px] rounded-full bg-accent/20 blur-[100px] pointer-events-none" />
      <motion.div animate={{ x: [0, -40, 0], y: [0, 40, 0] }} transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }} className="absolute bottom-1/4 left-[5%] w-[480px] h-[480px] rounded-full bg-olive-neon/25 blur-[120px] pointer-events-none" />

      <div className="relative h-full container-page grid lg:grid-cols-12 gap-8 items-center pt-28 z-10">
        <SlideLeft className="lg:col-span-7">
          <motion.div key={`eyebrow-${heroIdx}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="inline-flex w-fit items-center gap-2.5 px-4 py-2 rounded-full glass text-white/95 text-[11px] uppercase tracking-[0.28em]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-75" />
              <span className="relative rounded-full h-2 w-2 bg-accent" />
            </span>
            <Sparkles size={12} className="text-accent" />
            <EditableText value={slide.eyebrow} onChange={(t) => updateSlide({ eyebrow: t })} as="span" />
          </motion.div>

          <motion.h1 key={`h1-${heroIdx}`} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }} className="mt-6 font-display text-white font-semibold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-[1.1] tracking-tight">
            <span className="block">
              <EditableText value={slide.title_parts?.[0]} onChange={(t) => updateSlideTitle(0, t)} as="span" />
            </span>
            <span className="block">
              <EditableText value={slide.title_parts?.[1]} onChange={(t) => updateSlideTitle(1, t)} as="span" />
              <span className="relative inline-block italic pr-3">
                <span className="relative z-10 text-gradient drop-shadow-[0_0_30px_rgba(197,120,99,0.6)]">
                  <EditableText value={slide.title_parts?.[2]} onChange={(t) => updateSlideTitle(2, t)} as="span" />
                </span>
              </span>
            </span>
          </motion.h1>

          <motion.div key={`cta-${heroIdx}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }} className="mt-10 flex flex-wrap items-center gap-4">
            <div className="inline-flex flex-col">
              <Link to={slide.primary_cta?.href || '#'} className="group relative inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-br from-terracotta via-terracotta to-terracotta-deep text-cream font-bold text-sm tracking-wide shadow-[0_20px_50px_-12px_rgba(197,120,99,0.7)] hover:shadow-[0_30px_60px_-12px_rgba(197,120,99,0.95)] hover:-translate-y-0.5 transition-all duration-300 overflow-hidden">
                <span className="absolute inset-0 bg-gradient-to-r from-terracotta-deep via-terracotta to-cream bg-[length:200%_100%] animate-gradient-shift opacity-0 group-hover:opacity-100 transition-opacity" />
                <span className="relative">
                  <EditableText value={slide.primary_cta?.label} onChange={(t) => updateSlide({ primary_cta: { ...slide.primary_cta, label: t } })} as="span" />
                </span>
                <ArrowRight size={16} className="relative transition-transform group-hover:translate-x-1" />
              </Link>
              <HrefPill value={slide.primary_cta?.href} onChange={(h) => updateSlide({ primary_cta: { ...(slide.primary_cta || {}), href: h } })} />
            </div>
            <div className="inline-flex flex-col">
              <Link to={slide.secondary_cta?.href || '#'} className="group inline-flex items-center gap-2 px-8 py-4 rounded-full glass text-white font-semibold text-sm tracking-wide hover:border-accent/60 hover:text-accent transition-all duration-300">
                <EditableText value={slide.secondary_cta?.label} onChange={(t) => updateSlide({ secondary_cta: { ...slide.secondary_cta, label: t } })} as="span" />
                <ArrowUpRight size={16} className="transition-transform group-hover:rotate-45" />
              </Link>
              <HrefPill value={slide.secondary_cta?.href} onChange={(h) => updateSlide({ secondary_cta: { ...(slide.secondary_cta || {}), href: h } })} />
            </div>
          </motion.div>

          <div className="mt-10 flex items-center gap-3">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setHeroIdx(i)}
                className={`h-[3px] transition-all duration-500 ${heroIdx === i ? 'w-14 bg-accent shadow-[0_0_16px_rgba(197,120,99,0.7)]' : 'w-8 bg-white/40 hover:bg-white/70'}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
            <span className="ml-2 text-[10px] font-mono text-white/60 tracking-[0.22em]">
              {String(heroIdx + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
            </span>
          </div>

          {/* Mobile-only photo collage — mirrors desktop richness */}
          <div className="lg:hidden mt-8 relative h-[320px] sm:h-[380px]">
            <motion.div initial={{ opacity: 0, rotate: -6, y: 30 }} animate={{ opacity: 1, rotate: -3, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="absolute top-0 left-0 w-[78%] h-[72%] rounded-[22px] overflow-hidden border border-white/15 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] z-10">
              <AnimatePresence mode="wait">
                <motion.img key={`m-main-${heroIdx}`} src={slide.image?.src || slide.bg_image?.src} alt={slide.image?.alt || ''} initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }} className="h-full w-full object-cover" />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-olive-footer/60 via-transparent to-transparent" />
              <span className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-accent" />
              <span className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-accent" />
            </motion.div>

            <motion.div initial={{ opacity: 0, rotate: 10, x: 30 }} animate={{ opacity: 1, rotate: 4, x: 0 }} transition={{ duration: 0.8, delay: 0.35 }} className="absolute bottom-0 right-0 w-[55%] h-[50%] rounded-[18px] overflow-hidden border-2 border-accent/40 shadow-[0_20px_40px_-12px_rgba(197,120,99,0.4)] z-20">
              <AnimatePresence mode="wait">
                <motion.img key={`m-inset-${heroIdx}`} src={slide.inset_image?.src || slide.image?.src} alt={slide.inset_image?.alt || ''} initial={{ opacity: 0, scale: 1.15 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="h-full w-full object-cover" />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.5, ease: [0.34, 1.56, 0.64, 1] }} className="absolute bottom-2 left-2 z-30 backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-3 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)]">
              <div className="flex items-baseline gap-1">
                <span className="font-display text-3xl font-bold text-accent leading-none">{data.stat?.value}</span>
                <span className="font-display text-lg font-bold text-accent leading-none">{data.stat?.suffix}</span>
              </div>
              <div className="mt-1 text-[9px] uppercase tracking-[0.2em] text-white/80 font-semibold">
                {(data.stat?.label_lines || []).map((l, i, arr) => (
                  <React.Fragment key={i}>{l}{i < arr.length - 1 && <br />}</React.Fragment>
                ))}
              </div>
            </motion.div>

            {(data.chips || []).slice(0, 1).map((chip, i) => (
              <motion.div key={`m-chip-${i}`} initial={{ opacity: 0, scale: 0, rotate: 12 }} animate={{ opacity: 1, scale: 1, rotate: 6 }} transition={{ duration: 0.5, delay: 0.7, ease: [0.34, 1.56, 0.64, 1] }} className="absolute top-2 right-0 z-30 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent text-ink font-bold text-[10px] shadow-[0_10px_30px_-8px_rgba(197,120,99,0.7)]">
                <IconByName name={chip.icon} size={12} /> {chip.label}
              </motion.div>
            ))}
          </div>
        </SlideLeft>

        <SlideRight className="lg:col-span-5 hidden lg:block">
          <div className="relative h-[560px]">
            <div className="absolute -left-10 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-3 [writing-mode:vertical-rl] rotate-180">
              <EditableText
                value={data.established_label}
                onChange={(t) => update({ established_label: t })}
                as="span"
                className="text-[10px] font-mono tracking-[0.4em] text-white/50 uppercase"
              />
              <EditableText
                value={data.established_year}
                onChange={(t) => update({ established_year: t })}
                as="span"
                className="font-display text-accent text-2xl font-bold tracking-widest"
              />
              <span className="h-16 w-px bg-gradient-to-b from-accent to-transparent" />
            </div>

            <motion.div initial={{ opacity: 0, x: 30, y: 30 }} animate={{ opacity: 1, x: 0, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="absolute top-8 right-2 w-[78%] h-[78%] rounded-[32px] border-2 border-accent/70 z-0" />
            <div className="absolute top-12 right-6 w-[72%] h-[72%] rounded-[28px] border border-dashed border-white/15 z-0" />

            <motion.div initial={{ opacity: 0, rotate: -8, y: 40 }} animate={{ opacity: 1, rotate: -3, y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} className="absolute top-0 left-0 w-[68%] h-[68%] rounded-[28px] overflow-hidden border border-white/15 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)] z-10">
              <AnimatePresence mode="wait">
                <motion.img key={`main-${heroIdx}`} src={slide.image?.src} alt={slide.image?.alt || ''} initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }} className="h-full w-full object-cover" />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-olive-footer/60 via-transparent to-transparent" />
              <span className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-accent" />
              <span className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-accent" />
            </motion.div>

            <motion.div initial={{ opacity: 0, rotate: 12, x: 40 }} animate={{ opacity: 1, rotate: 4, x: 0 }} transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }} className="absolute bottom-4 right-0 w-[52%] h-[44%] rounded-[24px] overflow-hidden border-2 border-accent/40 shadow-[0_30px_60px_-20px_rgba(197,120,99,0.4)] z-20">
              <AnimatePresence mode="wait">
                <motion.img key={`inset-${heroIdx}`} src={slide.inset_image?.src} alt={slide.inset_image?.alt || ''} initial={{ opacity: 0, scale: 1.15 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="h-full w-full object-cover" />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.7, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55, ease: [0.34, 1.56, 0.64, 1] }} className="absolute bottom-2 left-2 z-30 backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-4 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)] w-[180px]">
              <div className="flex items-baseline gap-1">
                <EditableText
                  value={data.stat?.value}
                  onChange={(t) => update({ stat: { ...(data.stat || {}), value: t } })}
                  as="span"
                  className="font-display text-5xl font-bold text-accent leading-none"
                />
                <EditableText
                  value={data.stat?.suffix}
                  onChange={(t) => update({ stat: { ...(data.stat || {}), suffix: t } })}
                  as="span"
                  className="font-display text-2xl font-bold text-accent leading-none"
                />
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-white/80 font-semibold">
                {editMode ? (
                  <EditableText
                    value={(data.stat?.label_lines || []).join('\n')}
                    onChange={(t) => update({ stat: { ...(data.stat || {}), label_lines: t.split('\n') } })}
                    as="span"
                    multiline
                    className="whitespace-pre-line"
                  />
                ) : (
                  (data.stat?.label_lines || []).map((l, i, arr) => (
                    <React.Fragment key={i}>{l}{i < arr.length - 1 && <br />}</React.Fragment>
                  ))
                )}
              </div>
              <div className="mt-3 flex items-center gap-1.5 pt-3 border-t border-white/15">
                <StatIcon size={12} className="text-accent" />
                <EditableText
                  value={data.stat?.footer_label}
                  onChange={(t) => update({ stat: { ...(data.stat || {}), footer_label: t } })}
                  as="span"
                  className="text-[9px] uppercase tracking-[0.2em] text-white/60"
                />
              </div>
            </motion.div>

            {(data.chips || []).slice(0, 1).map((chip, i) => (
              <motion.div key={`chip-a-${i}`} initial={{ opacity: 0, scale: 0, rotate: 12 }} animate={{ opacity: 1, scale: 1, rotate: 6 }} transition={{ duration: 0.6, delay: 0.7, ease: [0.34, 1.56, 0.64, 1] }} className="absolute -top-3 right-4 z-30 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-accent text-ink font-bold text-xs shadow-[0_20px_40px_-12px_rgba(197,120,99,0.7)]">
                <IconByName name={chip.icon} size={14} />{' '}
                <EditableText
                  value={chip.label}
                  onChange={(t) => {
                    const next = [...(data.chips || [])];
                    next[i] = { ...(next[i] || {}), label: t };
                    update({ chips: next });
                  }}
                  as="span"
                />
              </motion.div>
            ))}

            <motion.div initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1, rotate: 360 }} transition={{ opacity: { duration: 0.5, delay: 0.9 }, scale: { duration: 0.5, delay: 0.9 }, rotate: { duration: 30, repeat: Infinity, ease: 'linear', delay: 1.4 } }} className="absolute top-[42%] left-[58%] z-30 w-24 h-24 rounded-full bg-white text-ink flex items-center justify-center shadow-[0_20px_40px_-12px_rgba(0,0,0,0.5)] border-2 border-accent">
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
                <defs>
                  <path id="circlePath" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
                </defs>
                <text fontSize="9" fontWeight="700" letterSpacing="2.5" fill="currentColor">
                  <textPath href="#circlePath">{data.sticker_text}</textPath>
                </text>
                {editMode && (
                  <foreignObject x="0" y="-18" width="100" height="18">
                    <input
                      type="text"
                      value={data.sticker_text || ''}
                      onChange={(e) => update({ sticker_text: e.target.value })}
                      className="w-full text-[8px] bg-yellow-50 border border-yellow-300 rounded px-1"
                      placeholder="sticker"
                    />
                  </foreignObject>
                )}
              </svg>
              <Star size={22} className="text-accent fill-accent" />
            </motion.div>

            {(data.chips || []).slice(1, 2).map((chip, i) => {
              const realIdx = i + 1;
              return (
                <motion.div key={`chip-b-${i}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.05 }} className="absolute -bottom-4 right-12 z-30 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-olive-deep text-white font-semibold text-xs shadow-[0_20px_40px_-12px_rgba(46,58,24,0.7)] border border-accent/40">
                  <IconByName name={chip.icon} size={14} className="text-accent" />{' '}
                  <EditableText
                    value={chip.label}
                    onChange={(t) => {
                      const next = [...(data.chips || [])];
                      next[realIdx] = { ...(next[realIdx] || {}), label: t };
                      update({ chips: next });
                    }}
                    as="span"
                  />
                </motion.div>
              );
            })}
          </div>
        </SlideRight>
      </div>

      {/* Hidden image-editor handles in edit mode */}
      {editing && (
        <div className="absolute top-32 left-1/2 -translate-x-1/2 z-40 flex gap-2 flex-wrap max-w-md justify-center">
          <div className="bg-white/95 px-2 py-1 rounded text-[10px] flex items-center gap-1">
            BG <EditableImage src={slide.bg_image?.src} alt={slide.bg_image?.alt} onChange={(src, alt) => updateSlide({ bg_image: { src, alt } })} className="w-10 h-10 object-cover rounded" />
          </div>
          <div className="bg-white/95 px-2 py-1 rounded text-[10px] flex items-center gap-1">
            Main <EditableImage src={slide.image?.src} alt={slide.image?.alt} onChange={(src, alt) => updateSlide({ image: { src, alt } })} className="w-10 h-10 object-cover rounded" />
          </div>
          <div className="bg-white/95 px-2 py-1 rounded text-[10px] flex items-center gap-1">
            Inset <EditableImage src={slide.inset_image?.src} alt={slide.inset_image?.alt} onChange={(src, alt) => updateSlide({ inset_image: { src, alt } })} className="w-10 h-10 object-cover rounded" />
          </div>
        </div>
      )}
    </section>
  );
}

export default HeroCarousel;
